import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { View } from '@smarterservices/smarter-ui';
import { debounce } from '@instructure/debounce';
/**
 * Get the total spacing (margin + border + padding) for a given CSS class
 * @param {string} className - The CSS class name to analyze
 * @returns {Object} Object with horizontal and vertical spacing values
 */
const getClassSpacing = (className) => {
  if (typeof document === 'undefined') {
    return { horizontal: 0, vertical: 0 };
  }

  const tempElement = document.createElement('div');
  tempElement.className = className;
  tempElement.style.visibility = 'hidden';
  tempElement.style.position = 'absolute';
  tempElement.style.top = '-9999px';
  document.body.appendChild(tempElement);

  try {
    const computedStyle = window.getComputedStyle(tempElement);
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
    const marginRight = parseFloat(computedStyle.marginRight) || 0;
    const marginTop = parseFloat(computedStyle.marginTop) || 0;
    const marginBottom = parseFloat(computedStyle.marginBottom) || 0;

    const horizontal = marginLeft + marginRight;
    const vertical = marginTop + marginBottom;

    return { horizontal, vertical };
  } finally {
    document.body.removeChild(tempElement);
  }
};

/**
 * WebRTC video renderer component that displays a LiveKit track in a standard HTML video element
 * @param {Object} props
 * @param {Object} props.trackRef - The LiveKit track reference from SessionTrack
 */
const Webrtc = ({ SessionTrack }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [containerStyle, setContainerStyle] = useState({});
  const [videoStyle, setVideoStyle] = useState({});
  const [classSpacing, setClassSpacing] = useState({ horizontal: 0, vertical: 0 });
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [videoMetadata, setVideoMetadata] = useState({ width: 1920, height: 1080 });
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });

  // Calculate spacing from the video container class
  useEffect(() => {
    const spacing = getClassSpacing('video-container-wrapper-mask');
    setClassSpacing(spacing);
  }, []);

  // Attach video track and get metadata
  useEffect(() => {
    if (SessionTrack?.webrtcRef?.publication?.track) {
      const videoTrack = SessionTrack?.webrtcRef?.publication?.track;
      if (videoRef.current) {
        // Attach the track to the video element
        videoTrack.attach(videoRef.current);

        // Get video dimensions when metadata loads
        const handleLoadedMetadata = () => {
          if (videoRef.current) {
            const width = videoRef.current.videoWidth || 1920;
            const height = videoRef.current.videoHeight || 1080;
            setVideoMetadata({ width, height });
            setIsVideoReady(true);
          }
        };

        videoRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);

        // Play the video
        videoRef.current.play().catch((error) => {
          console.error('Error playing video:', error);
        });

        return () => {
          if (videoRef.current) {
            videoRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
          }
        };
      }
    }

    return () => {
      if (SessionTrack?.webrtcRef?.publication?.track) {
        SessionTrack?.webrtcRef?.publication?.track.detach();
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
  }, [SessionTrack]);

  // Observe viewport size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const parentElement = containerRef.current.parentElement;
    if (!parentElement) return;

    // Initial size
    setViewportSize({
      width: parentElement.clientWidth,
      height: parentElement.clientHeight,
    });

    // Debounced resize handler for performance (16ms = ~60fps)
    const handleResize = debounce((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setViewportSize({ width, height });
      }
    }, 100);

    // Set up ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(parentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate container and video styles based on viewport and video dimensions
  useEffect(() => {
    if (!isVideoReady || !viewportSize.width || !viewportSize.height) return;

    // Calculate aspect ratios
    const videoAspectRatio = videoMetadata.width / videoMetadata.height;
    const viewportAspectRatio = viewportSize.width / viewportSize.height;

    let scaledWidth, scaledHeight;

    if (viewportAspectRatio > videoAspectRatio) {
      // Viewport is wider than video - fit to height
      scaledHeight = viewportSize.height;
      scaledWidth = viewportSize.height * videoAspectRatio;
    } else {
      // Viewport is taller than video - fit to width
      scaledWidth = viewportSize.width;
      scaledHeight = viewportSize.width / videoAspectRatio;
    }

    // Center the video in the viewport
    const horizontalOffset = (viewportSize.width - scaledWidth) / 2;
    const verticalOffset = (viewportSize.height - scaledHeight) / 2;

    // Set container style to position and size the video container
    setContainerStyle({
      position: 'absolute',
      width: `${scaledWidth - classSpacing.horizontal}px`,
      height: `${scaledHeight - classSpacing.vertical}px`,
      left: `${horizontalOffset}px`,
      top: `${verticalOffset}px`,
      overflow: 'hidden',
      backgroundColor: 'transparent',
    });

    // Set video style to fill the container
    setVideoStyle({
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: '0',
      top: '0',
      objectFit: 'fill',
      display: 'block',
    });
  }, [isVideoReady, videoMetadata, classSpacing, viewportSize]);

  return (
    <View
      as="div"
      position="relative"
      id={SessionTrack?.id || 'viewport'}
      className="webrtc-viewport"
      margin="auto"
      background="primary-inverse"
      width="100%"
      height="100%"
      elementRef={(ref) => {
        containerRef.current = ref;
      }}
      style={{
        overflow: 'hidden',
        contain: 'strict',
      }}
      withVisualDebug={false}
    >
      <div className="video-container-wrapper-mask" style={containerStyle}>
        <video
          ref={videoRef}
          style={videoStyle}
          muted
          playsInline
          autoPlay
        />
      </div>
    </View>
  );
};

export default Webrtc;
